import { cn } from '@/lib/utils';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { ComponentType, useState } from 'react';
import { Control } from 'react-hook-form';
import { Button } from '../ui/button';
import { FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

interface YearPickerControlProps {
  control?: Control<any>;
  name: string;
  label?: string | null;
  labelClass?: string;
  placeholder?: string;
  required?: boolean;
  className?: string;
  withoutDescription?: boolean;
  disabled?: boolean;
  minYear?: number;
  maxYear?: number;
}

export const YearPickerControl: ComponentType<YearPickerControlProps> = ({
  control,
  name,
  label,
  labelClass,
  placeholder = 'Yilni tanlang',
  required,
  className,
  withoutDescription,
  disabled,
  minYear = 2025,
  maxYear,
}) => {
  const currentYear = new Date().getFullYear();
  const [displayYear, setDisplayYear] = useState(currentYear);
  const startYear = Math.floor(displayYear / 10) * 10;
  const years = Array.from({ length: 10 }, (_, i) => startYear + i);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn(className, 'relative min-w-[140px]')}>
          {label && (
            <FormLabel className={labelClass}>
              {label} {required && <span className="text-red">*</span>}
            </FormLabel>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={'ghost'}
                disabled={disabled}
                className={cn(
                  'disabled:bg-gray-smoke/20 flex h-10 w-full items-center justify-between whitespace-nowrap rounded-lg border border-border bg-card px-3 py-1 text-sm text-foreground outline-none [&>span]:line-clamp-1',
                  className,
                )}
              >
                {field.value ? (
                  field.value
                ) : (
                  <span className="text-secondary/60">{placeholder}</span>
                )}
                <CalendarIcon
                  className={cn(
                    'ml-auto h-4 w-4',
                    field.value ? '' : 'opacity-50',
                  )}
                />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-full rounded-lg border-border bg-primary-foreground p-2"
              align="start"
            >
              <div className="flex items-center justify-between pb-2">
                <button
                  type="button"
                  className="rounded p-1 hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => setDisplayYear(displayYear - 10)}
                  disabled={startYear <= minYear}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm font-medium">
                  {startYear} - {startYear + 9}
                </span>
                <button
                  type="button"
                  className="rounded p-1 hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => setDisplayYear(displayYear + 10)}
                  disabled={maxYear !== undefined && startYear + 9 >= maxYear}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {years.map((year) => (
                  <button
                    key={year}
                    type="button"
                    disabled={
                      year < minYear ||
                      (maxYear !== undefined && year > maxYear)
                    }
                    onClick={() => {
                      field.onChange(year);
                    }}
                    className={cn(
                      'w-full rounded px-3 py-2 text-sm transition-colors hover:bg-gray-800',
                      field.value === year
                        ? 'bg-primary text-white'
                        : 'hover:bg-primary',
                      (year < minYear ||
                        (maxYear !== undefined && year > maxYear)) &&
                        'cursor-not-allowed opacity-50 hover:bg-transparent',
                    )}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          {!withoutDescription && (
            <div className="absolute -bottom-[19px] left-0">
              <FormMessage />
            </div>
          )}
        </FormItem>
      )}
    />
  );
};
